import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

import { SITE_URL } from "../config";

// Site-wide default meta. These used to be hardcoded in public/index.html, which
// produced duplicate <meta> tags alongside Helmet in the SPA. Managed here, Helmet
// keeps a single tag per name/property and page-level <Helmet> overrides them.
const DEFAULT_TITLE = "Distortion Grids Database";
const DEFAULT_DESCRIPTION =
  "Distortion Grids Database by Matchmove machine – ready-to-use lens grids and presets for precise 3D camera calibration in VFX and matchmoving.";
const DEFAULT_IMAGE = `${SITE_URL}/preview.jpg`;

// Exact paths that must not be indexed (own account dashboard).
const NOINDEX_EXACT = ["/profile"];

// Path prefixes that must not be indexed (auth, checkout, internal/dev).
// Note: "/profile/edit" is listed, but "/profile/<id>" (public creator
// profiles) is intentionally NOT matched, so those stay indexable.
const NOINDEX_PREFIXES = [
  "/profile/edit",
  "/authorization",
  "/registration",
  "/reset-password",
  "/checkout",
  "/payment",
  "/paddle-test",
  "/main-preview",
  "/glavni",
  "/google/oauth",
  "/facebook/oauth",
];

const isNoindex = (path) =>
  NOINDEX_EXACT.includes(path) ||
  NOINDEX_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

/**
 * Route-level SEO tags rendered once inside the Router.
 *
 * Emits a self-referencing canonical for every public route (query string is
 * stripped, which consolidates filtered Library URLs like
 * `/?full_price__gte=10` to `/`). Private/utility routes get a
 * noindex robots tag instead -- belt-and-suspenders with robots.txt, since
 * robots.txt only blocks crawling, not indexing of linked URLs.
 */
export default function RouteMeta() {
  const { pathname } = useLocation();
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const noindex = isNoindex(path);

  return (
    <Helmet>
      {/* Site-wide default description + Open Graph / Twitter tags, emitted on
          every route. Page-level <Helmet> (e.g. Product, News) overrides these
          via react-helmet-async "last wins" by name/property. */}
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={DEFAULT_TITLE} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={DEFAULT_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={DEFAULT_TITLE} />
      <meta name="twitter:description" content={DEFAULT_DESCRIPTION} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />

      {/* Indexable routes self-canonicalize (query stripped); private/utility
          routes get noindex instead. */}
      {noindex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <link rel="canonical" href={`${SITE_URL}${path}`} />
      )}
    </Helmet>
  );
}
